import {
    Stack,
    Button,
    ToggleButtonGroup,
    ToggleButton,
    IconButton,
} from "@mui/material";
import DateRangeSelector from "../../../components/DateRangeSelector";
import KeySelector from "./KeySelector";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import { DownloadCsvButton } from "../../../components/DownloadCsvButton";
import { useMemo } from "react";

const TrendsFiltersBar = ({
    selectedKeys,
    setSelectedKeys,
    onKeyClose,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    timeAggType,
    onTimeAggChange,
    onOpenFilterDrawer,
    onSave,
    inputData,
    energyPriceData,
    environmentalData,
    facilityName,
}) => {
    const { t } = useTranslation();

    const csvData = useMemo(() => {
        if (
            !inputData?.timestamps &&
            !energyPriceData?.timestamps &&
            !environmentalData?.timestamps
        )
            return [];

        const dates = [
            ...new Set(
                [].concat(
                    inputData?.timestamps ?? [],
                    energyPriceData?.timestamps ?? [],
                    environmentalData?.timestamps ?? [],
                ),
            ),
        ].sort();

        return dates.map((date) => {
            const baseObject = {
                DATE: date.split("T")[0],
                ...(timeAggType === "h" && {
                    TIME: date.split("T")[1].split("+")[0],
                }),
                ENERGY_PRICE:
                    energyPriceData?.data?.find(
                        (data) => data.metadata.name === "Electricity",
                    )?.values[energyPriceData.timestamps.indexOf(date)] ?? null,
            };

            const environmentalObject =
                environmentalData?.data?.map((data) => {
                    return {
                        [data.metadata.key]:
                            data.values[inputData.timestamps.indexOf(date)] ??
                            null,
                    };
                }) ?? [];

            const inputObject =
                inputData?.data?.map((data) => {
                    const columnName = `${data.metadata.location} (${data.metadata.system} - ${data.metadata.name}) ${data.metadata.key}`;
                    return {
                        [columnName]:
                            data.values[inputData.timestamps.indexOf(date)] ??
                            null,
                    };
                }) ?? [];

            return Object.assign(
                baseObject,
                ...environmentalObject,
                ...inputObject,
            );
        });
    }, [inputData, energyPriceData, environmentalData, timeAggType]);

    return (
        <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ borderBottom: 2, borderColor: "divider", p: 2 }}
        >
            <KeySelector
                keys={selectedKeys}
                onChange={(newKeys) => setSelectedKeys(newKeys)}
                onClose={onKeyClose}
            />
            <DateRangeSelector
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={onFromDateChange}
                onToDateChange={onToDateChange}
                small={true}
                hideTitle={false}
            />
            <ToggleButtonGroup
                value={timeAggType}
                exclusive
                size="small"
                onChange={onTimeAggChange}
            >
                <ToggleButton value="h">{t("H")}</ToggleButton>
                <ToggleButton value="d">{t("D")}</ToggleButton>
                <ToggleButton value="m">{t("M")}</ToggleButton>
                <ToggleButton value="q">{t("Q")}</ToggleButton>
                <ToggleButton value="y">{t("Y")}</ToggleButton>
            </ToggleButtonGroup>
            <Button
                variant="contained"
                size="small"
                startIcon={<FilterAltIcon />}
                onClick={onOpenFilterDrawer}
            >
                {t("Filter")}
            </Button>
            <IconButton size="medium" color="primary" onClick={onSave}>
                <SaveIcon />
            </IconButton>
            <DownloadCsvButton
                arrayOfObjectsData={csvData}
                filename={`${facilityName}_trends_data_${fromDate.toISOString().split("T")[0]}_${toDate.toISOString().split("T")[0]}.csv`}
                buttonText={t("Download CSV")}
            />
        </Stack>
    );
};

export default TrendsFiltersBar;
